import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Award, CreditCard, Bell,
  Calendar, Brain, LogOut, Menu, X, Users, TrendingUp
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NAV = [
  { icon: LayoutDashboard, label: 'Dashboard',     path: '/parent/dashboard',      color: 'text-blue-300' },
  { icon: Award,           label: 'Results',        path: '/parent/results',       color: 'text-emerald-300' },
  { icon: TrendingUp,      label: 'Child Progress', path: '/parent/child-progress',color: 'text-cyan-300' },
  { icon: CreditCard,      label: 'Fees',           path: '/parent/fees',          color: 'text-amber-300' },
  { icon: Bell,            label: 'Announcements',  path: '/parent/announcements', color: 'text-rose-300' },
  { icon: Calendar,        label: 'Events',         path: '/parent/events',        color: 'text-sky-300' },
  { icon: Brain,           label: 'AI Guide',       path: '/parent/ai',            color: 'text-fuchsia-300' },
];

export default function ParentLayout() {
  const [open, setOpen] = useState(false);
  const { user, school, logout } = useAuth();
  const navigate = useNavigate();

  const firstName = user?.first_name || 'Parent';
  const lastName  = user?.last_name  || '';
  const initials  = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'P';
  const fullName  = `${firstName} ${lastName}`.trim();

  return (
    <div className="min-h-screen bg-[#EFF6FF] flex">
      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* ── Floating Sidebar ── */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-40 lg:z-auto
        h-screen flex-shrink-0
        transition-transform duration-300
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div
          className="m-3 w-[248px] rounded-[28px] flex flex-col overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #2563eb 0%, #1d58d4 50%, #1e40af 100%)',
            boxShadow: '0 20px 60px rgba(37, 99, 235, 0.35)',
            height: 'calc(100vh - 24px)',
          }}
        >
          {/* Brand row */}
          <div className="flex items-center justify-between px-5 pt-6 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-black text-sm tracking-wider">EDUVA</p>
                <p className="text-white/50 text-[10px] leading-tight">Parent Portal</p>
              </div>
            </div>
            <button className="lg:hidden text-white/60 hover:text-white p-1" onClick={() => setOpen(false)}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Parent Card */}
          <div className="px-4 pb-4">
            <div className="bg-white/10 border border-white/10 rounded-2xl p-3.5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center font-black text-white text-sm border border-white/20 flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-bold text-sm truncate leading-tight">{fullName}</p>
                <p className="text-white/50 text-[11px] truncate mt-0.5">{school?.name || 'Parent Portal'}</p>
              </div>
            </div>
          </div>

          <p className="px-5 text-white/40 text-[10px] font-bold uppercase tracking-widest mb-2">Navigation</p>

          {/* Nav Items */}
          <nav className="flex-1 overflow-y-auto px-3 space-y-0.5 pb-2">
            {NAV.map(({ icon: Icon, label, path, color }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setOpen(false)}
                className={({ isActive: a }) =>
                  `group flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-semibold transition-colors duration-200 ${
                    a
                      ? 'bg-white/20 text-white shadow-inner'
                      : 'text-white/65 hover:text-white hover:bg-white/10'
                  }`
                }
                data-testid={`parent-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${color} bg-white/10`}>
                  <Icon className="w-[18px] h-[18px]" />
                </div>
                <span className="flex-1">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="px-3 pb-4 pt-2 border-t border-white/10">
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-white/50 hover:text-white hover:bg-white/10 text-sm font-semibold transition-colors"
              data-testid="parent-logout-btn"
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/10">
                <LogOut className="w-[18px] h-[18px]" />
              </div>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="lg:hidden h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200 flex items-center gap-3 px-4 sticky top-0 z-20">
          <button onClick={() => setOpen(true)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <p className="font-black text-slate-800 text-sm tracking-wide">EDUVA</p>
          <div className="ml-auto w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center font-black text-[#2563eb] text-xs">
            {initials}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
